import type {
  MwQuantity,
  MwRangeQuantity,
  PowerMwBasisTreatment,
  WaterUseRangeAssumption,
} from '../../types/economicContribution';

type StaffingLoadBasis = Pick<
  MwQuantity | MwRangeQuantity,
  'basis' | 'sourceBasis' | 'basisTreatment' | 'basisSource' | 'basisConversionPue'
>;

function formatPue(pue: WaterUseRangeAssumption | undefined): string {
  if (!pue) return 'an unavailable PUE';
  const range = pue.low === pue.high ? `${pue.low}` : `${pue.low}–${pue.high}`;
  return `${range} PUE${pue.sourceLabel ? ` (${pue.sourceLabel})` : ` (${pue.source.replaceAll('-', ' ')})`}`;
}

export function staffingBasisLabel(load: StaffingLoadBasis): string {
  switch (load.basisTreatment) {
    case 'facility-as-utility-service-screening-proxy':
      return 'Facility demand used as a disclosed utility-service screening proxy';
    case 'unspecified-as-facility-as-utility-service-screening-proxy':
      return 'Basis not recorded; facility demand used as a disclosed screening default and utility-service proxy';
    case 'it-pue-to-facility-as-utility-service-screening-proxy':
      return `Recorded IT load converted with ${formatPue(load.basisConversionPue)}, then used as a utility-service screening proxy`;
    default:
      return load.basis.replaceAll('-', ' ');
  }
}

export function staffingBasisFactor(treatment: PowerMwBasisTreatment | undefined): string | null {
  switch (treatment) {
    case 'facility-as-utility-service-screening-proxy':
      return 'MW basis: recorded facility demand is used as a disclosed utility-service screening proxy.';
    case 'unspecified-as-facility-as-utility-service-screening-proxy':
      return 'MW basis: the unrecorded project basis is treated as facility demand only as a disclosed screening default, then used as a utility-service proxy.';
    case 'it-pue-to-facility-as-utility-service-screening-proxy':
      return 'MW basis: recorded IT load is explicitly converted to facility demand with PUE, then used as a disclosed utility-service screening proxy.';
    default:
      return null;
  }
}

export function staffingBasisLimitation(
  load: StaffingLoadBasis,
  screenLabel: 'staffing' | 'construction',
): string | null {
  switch (load.basisTreatment) {
    case 'facility-as-utility-service-screening-proxy':
      return `The recorded facility-demand MW is treated as utility-service MW only for this early-stage screen of ${screenLabel}; service reserve and other basis differences are not modelled.`;
    case 'unspecified-as-facility-as-utility-service-screening-proxy':
      return `The site has no recorded MW basis. This early-stage ${screenLabel} screen assumes the entered MW is facility demand and then treats it as utility-service MW; confirm the project basis and service reserve before relying on the result.`;
    case 'it-pue-to-facility-as-utility-service-screening-proxy':
      return `The recorded IT-load MW is converted to facility demand using ${formatPue(load.basisConversionPue)}, then treated as utility-service MW for this early-stage ${screenLabel} screen. PUE and service-reserve uncertainty remain explicit.`;
    default:
      return null;
  }
}
